/**
 * Install App Button for Settings/Profile
 * A menu-style button that appears in settings for users who dismissed the prompt
 * or want to install later
 */

import { useState } from 'react';
import { AppColors } from '../../theme/colors';
import { ChevronRightIcon } from '../../theme/icons';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { useLanguage } from '../../contexts/LanguageContext';

export function InstallAppButton() {
  const { t } = useLanguage();
  const {
    isIOS,
    isInstallable,
    canShowInstallInSettings,
    triggerInstall,
  } = usePWAInstall();

  const [showIOSModal, setShowIOSModal] = useState(false);
  const [showNotAvailable, setShowNotAvailable] = useState(false);

  if (!canShowInstallInSettings) return null;

  const handleClick = async () => {
    if (isIOS) {
      setShowIOSModal(true);
      return;
    }

    // Check if install is available (beforeinstallprompt fired)
    if (!isInstallable) {
      setShowNotAvailable(true);
      return;
    }

    await triggerInstall();
  };

  return (
    <>
      <button
        className="menu-button"
        onClick={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '16px 20px',
          border: 'none',
          backgroundColor: 'transparent',
          color: AppColors.textPrimary,
          fontSize: '15px',
          fontWeight: '500',
          cursor: 'pointer',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          transition: 'background 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{
            fontSize: '18px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '10px',
            backgroundColor: 'rgba(139, 92, 246, 0.15)',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </span>
          <span>{t('pwa.installApp')}</span>
        </div>
        <ChevronRightIcon size={20} color={AppColors.textSecondary} />
      </button>

      {/* Not Available Modal */}
      {showNotAvailable && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            backdropFilter: 'blur(4px)',
          }}
          onClick={() => setShowNotAvailable(false)}
        >
          <div
            style={{
              backgroundColor: AppColors.bgSecondary,
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '320px',
              width: '100%',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              fontWeight: '700',
              color: AppColors.textPrimary,
              textAlign: 'center',
            }}>
              {t('pwa.installNotAvailable')}
            </h3>

            <p style={{
              margin: '0 0 16px 0',
              fontSize: '14px',
              color: AppColors.textSecondary,
              textAlign: 'center',
              lineHeight: 1.5,
            }}>
              {t('pwa.installNotAvailableDesc')} {t('pwa.tryChrome')}
            </p>

            <p style={{
              margin: '0 0 16px 0',
              fontSize: '13px',
              color: AppColors.textMuted,
              textAlign: 'center',
              lineHeight: 1.4,
            }}>
              {t('pwa.bookmarkTip')}
            </p>

            <button
              onClick={() => setShowNotAvailable(false)}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: AppColors.accent,
                color: '#1e1b4b',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              {t('pwa.gotIt')}
            </button>
          </div>
        </div>
      )}

      {/* iOS Instructions Modal - Enhanced visual version */}
      {showIOSModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            backdropFilter: 'blur(8px)',
          }}
          onClick={() => setShowIOSModal(false)}
        >
          <div
            style={{
              backgroundColor: AppColors.bgSecondary,
              borderRadius: '24px',
              padding: '28px 24px',
              maxWidth: '340px',
              width: '100%',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with app icon */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: '24px',
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px',
                boxShadow: '0 8px 16px rgba(139, 92, 246, 0.3)',
              }}>
                <span style={{ fontSize: '24px', fontWeight: '800', color: 'white' }}>AI</span>
              </div>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: AppColors.textPrimary,
                textAlign: 'center',
              }}>
                {t('pwa.installTitle')}
              </h3>
              <p style={{
                margin: '6px 0 0 0',
                fontSize: '13px',
                color: AppColors.textMuted,
                textAlign: 'center',
              }}>
                {t('pwa.addToHomeScreen')}
              </p>
            </div>

            {/* Steps */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              marginBottom: '24px',
            }}>
              {/* Step 1 - Safari Share Button */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
                padding: '14px',
                backgroundColor: 'rgba(139, 92, 246, 0.08)',
                borderRadius: '14px',
                border: '1px solid rgba(139, 92, 246, 0.15)',
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  backgroundColor: '#007AFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {/* Safari Share Icon - accurate iOS style */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="10" width="16" height="12" rx="2" stroke="white" strokeWidth="2"/>
                    <path d="M12 2v12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M8 6l4-4 4 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: '600',
                    color: AppColors.textPrimary,
                    marginBottom: '2px',
                  }}>
                    {t('pwa.tapShare')}
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: '13px',
                    color: AppColors.textMuted,
                  }}>
                    {t('pwa.findInSafari')}
                  </p>
                </div>
              </div>

              {/* Step 2 - Add to Home Screen */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
                padding: '14px',
                backgroundColor: 'rgba(139, 92, 246, 0.08)',
                borderRadius: '14px',
                border: '1px solid rgba(139, 92, 246, 0.15)',
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  backgroundColor: AppColors.bgTertiary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  border: '1px solid rgba(255,255,255,0.1)',
                }}>
                  {/* Add to Home Screen Icon */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="4" stroke={AppColors.accent} strokeWidth="2"/>
                    <path d="M12 8v8M8 12h8" stroke={AppColors.accent} strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: '600',
                    color: AppColors.textPrimary,
                    marginBottom: '2px',
                  }}>
                    {t('pwa.addToHome')}
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: '13px',
                    color: AppColors.textMuted,
                  }}>
                    {t('pwa.scrollShareMenu')}
                  </p>
                </div>
              </div>

              {/* Step 3 - Confirm */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px',
                padding: '14px',
                backgroundColor: 'rgba(74, 222, 128, 0.08)',
                borderRadius: '14px',
                border: '1px solid rgba(74, 222, 128, 0.15)',
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(74, 222, 128, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {/* Checkmark Icon */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke={AppColors.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: '600',
                    color: AppColors.textPrimary,
                    marginBottom: '2px',
                  }}>
                    {t('pwa.tapAdd')}
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: '13px',
                    color: AppColors.textMuted,
                  }}>
                    {t('pwa.appWillAppear')}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowIOSModal(false)}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '14px',
                border: 'none',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
              }}
            >
              {t('pwa.gotIt')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default InstallAppButton;
